import { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { Users, Briefcase, Award, Building2 } from 'lucide-react';
import { AssignedToInfo, AssignedToType } from '../types';
import { SimpleTooltip } from '../../../common';

interface AssignedToCellProps {
  surveyId: string;
  assignedTo: AssignedToInfo;
}

const MAX_VISIBLE = 2;

const ICONS: Record<AssignedToType, React.ReactNode> = {
  individual: <Users className="w-4 h-4 text-[var(--color-primary-500)]" />,
  role: <Briefcase className="w-4 h-4 text-[var(--color-secondary-500)]" />,
  designation: <Award className="w-4 h-4 text-[var(--color-warning)]" />,
  department: <Building2 className="w-4 h-4 text-[var(--color-success)]" />,
};

const LABELS: Record<AssignedToType, string> = {
  individual: 'Individuals',
  role: 'Roles',
  designation: 'Designations',
  department: 'Departments',
};

const AssignedToCell: React.FC<AssignedToCellProps> = ({ assignedTo }) => {
  const { type, entities, totalCount } = assignedTo;

  const getDisplayText = () => {
    // Show up to 2 names, then add ellipsis if more exist
    const names = entities.slice(0, 2).map(e => e.name);
    let text = names.join(', ');

    if (entities.length > 2 || totalCount > 2) {
      text += '...';
    }

    return text || 'N/A';
  };

  return (
    <div className="flex items-center gap-2 max-w-full">
      <div className="flex-shrink-0">
        {ICONS[type] || <Users className="w-3.5 h-3.5 text-[var(--color-text-tertiary)]" />}
      </div>
      <SimpleTooltip
        closeOnScroll={false}
        tooltipClassName={`${assignedTo.entities.length > 2 || assignedTo.totalCount > 2 ? 'mt-2' : 'hidden'}`}
        delay={150}
        side="top"
        label={
          <div className="max-w-xs p-2">
            <div className="text-caption font-medium text-[var(--color-text-secondary)] mb-2">
              Assigned Users ({entities.length})
            </div>

            <div className="flex flex-col gap-[2px] max-h-48 overflow-y-auto pr-1">
              {entities.map((e, i) => (
                <div
                  key={i}
                  className="text-body-sm text-[var(--color-text-strong)] px-2 py-1 rounded hover:bg-[var(--color-bg-muted)]"
                >
                  {e.name}
                </div>
              ))}
            </div>
          </div>
        }
      >
        <span
          className="text-body-sm text-[var(--color-text-strong)] truncate font-normal"
        >
          {getDisplayText()}
        </span>
      </SimpleTooltip>
      {totalCount > 0 && (
        <span className="text-caption text-[var(--color-text-tertiary)] font-medium">
          ({totalCount})
        </span>
      )}
    </div>
  );
};

export { LABELS, MAX_VISIBLE };
export default AssignedToCell;
